// @mui material components
import { Grid, ToggleButton, ToggleButtonGroup, CircularProgress, Paper } from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import "wx-react-gantt/dist/gantt.css";
// Chart.js related imports
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
// import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import BubbleChart from "examples/Charts/BubbleChart";
import MDTypography from "components/MDTypography";
import MyGantt from "components/Gantt";
import { useEffect, useState, useRef } from "react";
import { getDepartmentById, getDashboardPageData } from "services/api";

import { Typography, Box } from "@mui/material";
import { useAuth } from "context/authContext";
import BranchPerformanceMap from "components/BranchPerformanceMap";
import EgyptMap2 from "components/Map2";
import { calculateAverageLocation } from "services/utils";
import { useTranslation } from "react-i18next";
// const [loading, setLoading] = useState(false);
import { fetchRiskMatrices } from "services/utils";
import PerformanceGaugeCard from "../PerformanceGauge";
import PerformanceChartCard from "components/EmployeePerformanceChart";
import { useAppTheme } from "context/themeContext";

function Corporate() {
  const { t, i18n } = useTranslation();
  const [departmentFilter, setDepartmentFilter] = useState("");
  const { primaryColor } = useAppTheme();
  const { employee, selectedMonth, selectedYear, setSelectedMonth, setSelectedYear, companies } =
    useAuth();

  const infoCircles = [
    {
      title: t("user.dashboards.mission"),
      content: companies[0].mission,
      icon: <FlagIcon fontSize="medium" />, // 👈 set size here
      color: "#e3f2fd",
      borderColor: "#2196f3",
      borderRadius: "12px",
      background: "#ffffff",
      boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
      border: "1px solid",
    },
    {
      title: t("user.dashboards.vision"),
      content: companies[0].vision,
      icon: <VisibilityIcon fontSize="medium" />,
      color: "#e8f5e9",
      borderColor: "#4caf50",
      borderRadius: "12px",
      background: "#ffffff",
      boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
      border: "1px solid",
    },
    {
      title: t("user.dashboards.purpose"),
      content: companies[0].purpose,
      icon: <EmojiObjectsIcon fontSize="medium" />,
      color: "#fff8e1",
      borderColor: "#fbc02d",
      borderRadius: "12px",
      background: "#ffffff",
      boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
      border: "1px solid",
    },
  ];

  const [distribution, setDistribution] = useState();
  const [branches, setBranches] = useState([]);
  const [totalResult, setTotalResult] = useState(null);
  const [areas, setAreas] = useState([]);
  const [view, setView] = useState("areas"); // Default view
  const [initiatives, setInitiatives] = useState([]);
  const [center, setCenter] = useState();
  const [areaCenter, setAreaCenter] = useState();
  const [riskMatrices, setRiskMatrices] = useState([]);
  const [riskMatrixChart, setRiskMatrixChart] = useState([]);

  useEffect(() => {
    setDepartmentFilter("");
    if (employee?.role != 1) {
      getDepartmentById(employee?.departmentId).then((e) => {
        setDepartmentFilter(e.departmentName);
      });
    }
  }, []);

  //Use Effect
  useEffect(() => {
    const fetchData = async () => {
      try {
        setTotalResult(0);
        setDistribution(null);
        setBranches(null);
        setAreas(null);
        const companyId = employee.companyId;

        const [pageData, riskMatricesData] = await Promise.all([
          getDashboardPageData(selectedYear, selectedMonth, employee.id, companyId), // new unified endpoint
          fetchRiskMatrices(setRiskMatrices, setRiskMatrixChart), // still called as before
        ]);

        if (pageData === "error") return;

        // --- Distribution Chart ---
        // if (responseDistribution !== "error") {
        if (pageData.averageStatus?.length > 0) {
          // let filteredData = responseDistribution;
          let filteredData = pageData.averageStatus || [];
          if (departmentFilter) {
            // filteredData = responseDistribution?.filter(
            filteredData = filteredData.filter((entry) => entry.split(",")[2] === departmentFilter);
          }

          const scores = filteredData
            ?.map((entry) => {
              const value = entry?.split(",")[1];
              const parsed = parseFloat(value);
              return isNaN(parsed) || parsed === 0 ? null : Math.round(parsed);
            })
            .filter((score) => score !== null);

          const countOccurrences = (data) =>
            data.reduce((acc, value) => {
              acc[value] = (acc[value] || 0) + 1;
              return acc;
            }, {});

          const countData = countOccurrences(scores);
          const uniqueScores = Object.keys(countData)
            .map(Number)
            .filter((score) => score !== 0)
            .sort((a, b) => a - b);

          const chartData = {
            labels: uniqueScores,
            datasets: [
              {
                label: t("user.dashboards.freq"),
                data: uniqueScores.map((score) => countData[score]),
                backgroundColor: "#3A6EA5",
                borderColor: "#3A6EA5",
                borderWidth: 1,
                fill: false,
                maxBarThickness: 35,
              },
            ],
          };
          setDistribution(chartData);
        } else {
          const chartData = {
            labels: [],
            datasets: [
              {
                label: t("user.dashboards.freq"),
                data: [],
                backgroundColor: "#3A6EA5",
                borderColor: "#3A6EA5",
                borderWidth: 1,
                fill: false,
                maxBarThickness: 35,
              },
            ],
          };
          setDistribution(chartData);
        }

        // --- Branches ---
        if (pageData.branches?.length > 0) {
          setBranches(pageData.branches);
          setCenter(calculateAverageLocation(pageData.branches));
        }

        if (pageData.areas?.length > 0) {
          setAreas(pageData.areas);
          setAreaCenter(calculateAverageLocation(pageData.areas));
        }

        if (pageData.initiatives?.length > 0) {
          setInitiatives(pageData.initiatives);
        }

        if (pageData.individualData?.length > 0 && pageData.individualData[0].length > 4) {
          setTotalResult(pageData.individualData[0][3]);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      }
    };

    fetchData();
  }, [selectedMonth, selectedYear]);

  const handleMonthChange = (event) => setSelectedMonth(event.target.value);
  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const normalizeScore = (score) => {
    if (score === 0) {
      return 0;
    }
    if (score <= 70) {
      return (score / 70) * 0.2; // Map 0-70 to 0%-20%
    } else if (score <= 80) {
      return 0.2 + ((score - 70) / 10) * 0.2; // Map 70-80 to 20%-40%
    } else if (score <= 90) {
      return 0.4 + ((score - 80) / 10) * 0.2; // Map 80-90 to 40%-60%
    } else if (score <= 105) {
      return 0.6 + ((score - 90) / 15) * 0.2; // Map 90-105 to 60%-80%
    } else {
      return 0.8 + ((score - 105) / 15) * 0.2; // Map 105-120 to 80%-100%
    }
  };

  return (
    <>
      <Grid container spacing={4} mt={0} justifyContent="center">
        {infoCircles.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} display="flex" justifyContent="center" key={index}>
            <Box
              sx={{
                width: "clamp(200px, 70%, 240px)",
                minHeight: 180,
                backgroundColor: "background.paper",
                borderRadius: 3,
                boxShadow: `0 3px 10px ${item.borderColor}33`,
                overflow: "hidden", // 🔥 important
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",

                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                ":hover": {
                  transform: "translateY(-4px)",
                  boxShadow: `0 8px 20px ${item.borderColor}55`,
                },
              }}
            >
              {/* 🔥 TOP SECTION (with padding) */}
              <Box sx={{ p: 0.5, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ color: item.borderColor, mb: 0.25 }}>{item.icon}</Box>
              </Box>

              {/* 🔥 FULL WIDTH LINE */}
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: item.borderColor,
                  opacity: 0.6,
                }}
              />

              {/* 🔥 TITLE (with padding) */}
              <Box sx={{ p: 0.5, textAlign: "center" }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.title}
                </Typography>
              </Box>

              {/* 🔥 FULL WIDTH LINE */}
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  backgroundColor: item.borderColor,
                  opacity: 0.6,
                }}
              />

              {/* 🔥 CONTENT (with padding) */}
              <Box sx={{ py: 1, px: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    maxHeight: "100px",
                    overflowY: "auto",
                    fontSize: "0.8rem",
                    textAlign: "center",
                    pr: 2,
                  }}
                >
                  {item.content}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Grid
        container
        spacing={1}
        mt={0.5}
        alignItems="stretch"
        sx={{
          width: "100%",
          px: 2,
        }}
      >
        {/* LEFT SIDE: Risk + Gauge on top, Performance below */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {/* Top row: Risk Matrix + Gauge */}
            <Grid item xs={12} container spacing={2} sx={{ alignItems: "stretch" }}>
              {/* Risk Matrix */}
              <Grid item xs={12} sm={6}>
                <Paper
                  sx={{
                    px: 2, // Keep horizontal padding
                    pt: 2, // Keep top padding
                    pb: 0.5, // 👈 DRASTICALLY reduce bottom padding (from 16px to 4px)
                    borderRadius: "4px",
                    border: "1px solid #e5e7eb",
                    height: "100%",
                    boxShadow: "none",
                  }}
                >
                  {riskMatrixChart?.length > 0 ? (
                    <BubbleChart
                      title={t("user.dashboards.riskMatrix")}
                      height="100%"
                      processedData={riskMatrixChart}
                      labels={{
                        x: t("admin.strategicInfo.riskManagement.impact"),
                        y: t("admin.strategicInfo.riskManagement.likelihood"),
                      }}
                    />
                  ) : (
                    <MDBox height={100} display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress size={20} />
                    </MDBox>
                  )}
                </Paper>
              </Grid>

              {/* Performance Gauge */}
              <Grid item xs={12} sm={6}>
                <Paper
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    minHeight: 50,
                    borderRadius: "4px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "none",
                  }}
                >
                  <PerformanceGaugeCard
                    percent={normalizeScore(totalResult)}
                    arcWidth={0.2}
                    arcPadding={0.02}
                    needleColor="#000"
                    needleBaseColor="#000"
                    textColor="#000"
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Bottom row: Performance Chart */}
            <Grid item xs={12} md={12}>
              <Paper
                sx={{
                  p: 2,
                  height: "100%",
                  borderRadius: "4px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "none",
                }}
              >
                <PerformanceChartCard
                  index={2}
                  chartData={distribution}
                  title={t("user.dashboards.employeePerformance")}
                  t={t}
                />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        {/* RIGHT SIDE: Map */}
        <Grid item xs={12} md={4} sx={{ alignItems: "stretch" }}>
          <Paper
            elevation={3}
            sx={{
              borderRadius: "20px",
              p: { xs: 2, sm: 3 },
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
              position: "relative",
              // minHeight: { xs: 300, sm: 350 },
            }}
          >
            {/* Toggle Button Header */}
            <Box
              sx={{
                mb: 2,
                px: 1,
                py: 0.5,
                bgcolor: "#ffffffee",
                borderRadius: "999px",
                display: "inline-flex",
                boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={handleViewChange}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "none",
                    px: 2,
                    py: 0.5,
                    borderRadius: "999px !important",
                    color: "text.primary",

                    fontSize: "0.75rem", // 👈 smaller text
                    fontWeight: 500,
                    minHeight: "32px", // 👈 controls button height
                    transition: "all 0.2s ease-in-out",
                    "&.Mui-selected": {
                      background: primaryColor,
                      color: "#fff",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                    },
                  },
                }}
              >
                <ToggleButton value="areas">{t("user.dashboards.areas")}</ToggleButton>
                <ToggleButton value="branches">{t("user.dashboards.branches")}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Map Display */}
            <Box
              sx={{
                width: "100%",
                borderRadius: "20px",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 0,
                // minHeight: { xs: 200, sm: 250 },
              }}
            >
              {view === "branches" && center && branches ? (
                <BranchPerformanceMap
                  branches={branches}
                  month={selectedMonth}
                  year={selectedYear}
                  center={center}
                />
              ) : areaCenter && areas ? (
                <EgyptMap2
                  areas={areas}
                  center={areaCenter}
                  month={selectedMonth}
                  year={selectedYear}
                />
              ) : (
                <CircularProgress size={28} />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Gantt Chart */}
      <Box sx={{ px: 2 }}>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Paper
              sx={{
                p: { xs: 2, sm: 3 },
                height: "100%",
                overflowX: "auto",
                borderRadius: "4px",
                border: "1px solid #e5e7eb",
                boxShadow: "none",
                maxHeight: 400,
              }}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t("user.dashboards.corporateInitiatives")}
              </Typography>

              {initiatives ? (
                <Box sx={{ mt: 1 }}>
                  <MyGantt initiatives={initiatives} />
                </Box>
              ) : (
                <MDBox height="100%" display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress size={20} />
                </MDBox>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
export default Corporate;
